import { Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { NAME_PATTERN, NAME_RULE } from './shared/validation/name-validation';

type NestedObject = { [key: string]: any };


@Injectable({
  providedIn: 'root'
})
export class ConstantsService {
  private constantsUrl = this.getConstantsUrl();
  // 'http://192.168.68.67:8888/application-constants/dev'; // URL to web API
  private constants: any = {};
  private ready = new BehaviorSubject<boolean>(false);
  private readyPromise: Promise<void>;

  constructor(private http: HttpClient) {
    this.fetchConstants();

    this.readyPromise = new Promise((resolve) => {
      this.ready.subscribe(ready => {
        if (ready) {
          resolve();
        }
      });
    });
  }

  public waitForConstants(): Promise<void> {
    return this.readyPromise;
  }

  private fetchConstants(): void {
    this.http.get<any>(this.constantsUrl).pipe(
      tap(data => {
        this.constants = {
          ...data.propertySources[0].source,
          // The legacy server pattern rejects ordinary personal/business names.
          namePattern: NAME_PATTERN,
          nameRule: NAME_RULE,
        };
        this.applyLocalProxyOverrides();
        this.ready.next(true);
      }),
      catchError(this.handleError<any>('fetchConstants', {}))
    ).subscribe();
  }

  private getConstantsUrl(): string {
    const hostname = window.location.hostname;
    const isTunnelHost = hostname.endsWith('.jomisacu.work');
    const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname);
    const shouldUseDevProxy = !environment.production && (isLocalhost || isTunnelHost);

    if (!shouldUseDevProxy || !environment.apiUrl.startsWith('http')) {
      return environment.apiUrl;
    }

    const url = new URL(environment.apiUrl);
    return `${url.pathname}${url.search}`;
  }

  hasConstant(key: string): boolean {
    return key in this.constants;
  }

  getConstant(key: string): any {
    if (Object.prototype.hasOwnProperty.call(this.constants, key)) {
      return this.constants[key];
    }

    return this.getNestedObject(key) || this.halt(key);
  }

  private halt(key: string): never {
    if (isDevMode()) {
      const message = `
    <h1>Missing key in constants</h1>
    <p>Can't find <strong style="color: red;">${key}</strong> in constants. Please check the key.</p>
    <h2>Why am I seeing this?</h2>
    <p>This error occurs when the ConstantsService service is not ready yet or the key you are trying to access for does not exist in the constants.</p>
    <p>Since constants are initialized before anything in the app, there is only one reason: CONSTANT NOT EXIST:</p>
    `;
      setTimeout(() => document.write(message), 1000);
    }

    throw new Error("Can't ready " + key + " from constants. Constants not loaded yet. Please wait for the service to be ready.");
  }

  isReady(): Observable<boolean> {
    return this.ready.asObservable();
  }

  onReady(callback: () => void): void {
    this.readyPromise.then(callback);
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
        console.error(`${operation} failed: ${error.message}`);

        return of(result as T);
    };
  }

  private applyLocalProxyOverrides(): void {
    const hostname = window.location.hostname;
    const isTunnelHost = hostname.endsWith('.jomisacu.work');
    const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname);
    const shouldUseDevProxy = !environment.production && (isLocalhost || isTunnelHost);
    const configuredApiUrl = this.constants?.apiUrl;

    if (!shouldUseDevProxy || typeof configuredApiUrl !== 'string') {
      return;
    }

    if (configuredApiUrl.startsWith('http://') || configuredApiUrl.startsWith('https://')) {
      this.constants.apiUrl = '';
    }
  }

  private getConstantsByPrefix(prefix: string) {
    let constants: { [key: string]: string  } = {};
    for (const key in this.constants) {
      if (key.startsWith(prefix)) {
        constants[key] = this.constants[key];
      }
    }

    return constants;
  }

  private getNestedObject(propertyName: string): NestedObject | null {
    const constants = this.getConstantsByPrefix(propertyName + "[");

    if (Object.keys(constants).length === 0) {
      return null;
    }

    const constantsAsText = Object.entries(constants).map(([key, value]) => {
      return `"${key}": "${value}"`;
    });

    return this.parseNestedObjectFromFlatKeys(
      constantsAsText.join("\n")
    )[propertyName];
  }

  private parseNestedObjectFromFlatKeys(flatText: string): NestedObject {
    const obj: NestedObject = {};

    const lines = flatText.trim().split('\n');
    for (const line of lines) {
      const match = line.match(/^"(.+?)":\s*"(.+?)",?$/);
      if (!match) continue;

      const path = match[1];
      const value = match[2];

      const parts = path
        .replace(/\[(\d+)\]/g, '.$1') // Convert array indices to dot notation
        .split('.');

      let current: any = obj;

      for (let i = 0; i < parts.length; i++) {
        const isIndex = /^\d+$/.test(parts[i]);
        const part = isIndex ? parseInt(parts[i]) : parts[i];

        if (i === parts.length - 1) {
          current[part] = value;
        } else {
          if (!(part in current)) {
            current[part] = /^\d+$/.test(parts[i + 1]) ? [] : {};
          }
          current = current[part];
        }
      }
    }

    return obj;
  }
}
